// packages/web/src/pages/ForgotPasswordPage.tsx
import React, { useState, useRef, useEffect } from 'react';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { AuthService } from '@matchpoint/shared';

// Styled components matching landing page
const PageContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  minHeight: '100vh',
  backgroundColor: '#fafafa',
});

const ForgotPasswordContainer = styled(Container)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '100vh',
  padding: theme.spacing(3),
}));

const ForgotPasswordPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(5),
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  maxWidth: 480,
  width: '100%',
  borderRadius: '16px',
  border: '1px solid #e5e5e5',
  boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
  backgroundColor: '#ffffff',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(3),
  },
}));

const LogoContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: theme.spacing(4),
  gap: theme.spacing(1.5),
}));

const LogoImage = styled('div')(({ theme }) => ({
  width: 48,
  height: 48,
  borderRadius: '50%',
  background: 'linear-gradient(135deg, #84b817 0%, #c4d82f 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative',
  boxShadow: '0 2px 8px rgba(132, 184, 23, 0.3)',
  '&::before': {
    content: '""',
    position: 'absolute',
    width: 18,
    height: 18,
    borderRadius: '50%',
    backgroundColor: '#c4d82f',
    top: 8,
    left: 8,
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    width: 14,
    height: 14,
    borderRadius: '50%',
    backgroundColor: '#679614',
    bottom: 8,
    right: 8,
  },
}));

const LogoText = styled(Typography)(({ theme }) => ({
  fontSize: '1.75rem',
  fontWeight: 700,
  color: '#1a1a1a',
  fontFamily: '"Helvetica Neue", Arial, sans-serif',
  letterSpacing: '-0.03em',
}));

const PageTitle = styled(Typography)(({ theme }) => ({
  fontSize: '2rem',
  fontWeight: 700,
  marginBottom: theme.spacing(1),
  color: '#1a1a1a',
  letterSpacing: '-0.02em',
  textAlign: 'center',
  [theme.breakpoints.down('sm')]: {
    fontSize: '1.75rem',
  },
}));

const PageSubtitle = styled(Typography)(({ theme }) => ({
  fontSize: '1rem',
  color: '#666',
  marginBottom: theme.spacing(4),
  textAlign: 'center',
  fontWeight: 400,
  lineHeight: 1.6,
}));

const PrimaryButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#c4d600',
  color: '#1a1a1a',
  fontSize: '1rem',
  fontWeight: 700,
  padding: theme.spacing(1.5, 3),
  borderRadius: '8px',
  textTransform: 'none',
  boxShadow: '0 4px 12px rgba(196, 214, 0, 0.25)',
  border: 'none',
  '&:hover': {
    backgroundColor: '#b3c200',
    boxShadow: '0 6px 16px rgba(196, 214, 0, 0.35)',
    transform: 'translateY(-1px)',
  },
  '&:disabled': {
    backgroundColor: '#e5e5e5',
    color: '#999',
  },
  transition: 'all 0.2s ease',
}));

const SecondaryButton = styled(Button)(({ theme }) => ({
  backgroundColor: 'transparent',
  color: '#1a1a1a',
  fontSize: '1rem',
  fontWeight: 600,
  padding: theme.spacing(1.5, 3),
  borderRadius: '8px',
  border: '2px solid #e5e5e5',
  textTransform: 'none',
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
    borderColor: '#1a1a1a',
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    backgroundColor: '#fafafa',
    '& fieldset': {
      borderColor: '#e5e5e5',
    },
    '&:hover fieldset': {
      borderColor: '#c4d600',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#c4d600',
      borderWidth: '2px',
    },
  },
  '& .MuiInputLabel-root': {
    color: '#666',
    '&.Mui-focused': {
      color: '#1a1a1a',
    },
  },
}));

export const ForgotPasswordPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showCaptcha, setShowCaptcha] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }

    if (showCaptcha && !captchaToken) {
      setError('Please complete the security verification');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      const authService = AuthService.getInstance();
      const result = await authService.sendPasswordReset(
        email, 
        captchaToken || undefined
      );

      if (!isMountedRef.current) return;

      if (result.requiresCaptcha && !showCaptcha) {
        setShowCaptcha(true);
        setError('Additional security verification required');
      } else if (result.success) {
        setSuccess(true);
        setError('');
      }

    } catch (error: any) {
      if (!isMountedRef.current) return;
      setError(error.message);
      setCaptchaToken(null);
    } finally {
      if (!isMountedRef.current) return;
      setLoading(false);
    }
  };

  const handleCaptchaChange = (token: string | null) => {
    setCaptchaToken(token);
  };

  return (
    <PageContainer>
      <ForgotPasswordContainer>
        <ForgotPasswordPaper>
          <LogoContainer>
            <LogoImage />
            <LogoText>sched tennis</LogoText>
          </LogoContainer>

          <PageTitle>Forgot your password?</PageTitle>
          <PageSubtitle>
            No worries! Enter your email and<br />we'll send you a reset link.
          </PageSubtitle>

          {error && (
            <Alert severity="error" sx={{ width: '100%', mb: 3, borderRadius: '8px' }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ width: '100%', mb: 3, borderRadius: '8px' }}>
              Password reset instructions sent! Check your email.
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
            <StyledTextField
              fullWidth
              label="Email Address"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              margin="normal"
              required
              autoComplete="email"
              disabled={loading}
            />

            {showCaptcha && (
              <Box sx={{ 
                mt: 3, 
                p: 2.5, 
                bgcolor: '#fff3cd', 
                border: '1px solid #fbbf24', 
                borderRadius: '8px' 
              }}>
                <Typography variant="body2" sx={{ color: '#856404', mb: 1.5, fontWeight: 600 }}>
                  Please complete verification to continue:
                </Typography>
                <Box sx={{ 
                  bgcolor: '#f5f5f5', 
                  p: 3, 
                  borderRadius: '8px', 
                  textAlign: 'center',
                  border: '1px solid #e5e5e5'
                }}>
                  <Typography variant="body2" color="text.secondary">
                    CAPTCHA verification would go here
                  </Typography>
                  <Button
                    size="small"
                    onClick={() => setCaptchaToken('dummy-token')}
                    sx={{ 
                      mt: 2, 
                      textDecoration: 'underline', 
                      color: '#84b817',
                      fontWeight: 600 
                    }}
                  >
                    Click to verify (demo)
                  </Button>
                </Box>
              </Box>
            )}

            <PrimaryButton
              type="submit"
              fullWidth
              disabled={loading || (showCaptcha && !captchaToken)}
              sx={{ mt: 3, mb: 2 }}
            >
              {loading ? 'Sending...' : 'Send Reset Email'}
            </PrimaryButton>

            <SecondaryButton
              fullWidth
              href="/login"
            >
              Back to Sign In
            </SecondaryButton>
          </Box>
        </ForgotPasswordPaper>
      </ForgotPasswordContainer>
    </PageContainer>
  );
};